import { GridColDef } from '@mui/x-data-grid';

export type TabKey = 'BRAND' | 'MODEL' | 'EMI_BANK' | 'SUPPLIER' | 'CUSTOMER';

export type FieldType = 'text' | 'number' | 'select' | 'phone' | 'email' | 'state';

export interface FieldConfig {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  /** Where a select gets its options. */
  source?: 'brands';
  /**
   * For a 'state' field: the key that receives the state's name, while this field
   * holds its GST code. Both are stored, so a bill can print "Tamil Nadu (33)"
   * without the screen having to look the name up again.
   */
  nameKey?: string;
  /** Included in the duplicate check call, so "ON8" is only duplicate within its own brand. */
  partOfDuplicateKey?: boolean;
}

export interface MasterConfig {
  /** URL segment: /mobile-shop/v1/{path} */
  path: string;
  label: string;
  title: string;
  /** The field the duplicate message and the delete confirmation name. */
  nameKey: string;
  searchPlaceholder: string;
  fields: FieldConfig[];
  columns: GridColDef[];
}

const centred = { flex: 1, align: 'center', headerAlign: 'center' } as const;

/**
 * The simple masters - the ones that are just a name and a few details.
 *
 * <p>Products are not here: they have their own screen, because a product carries
 * brand, model, specifications, prices and its batches.
 */
export const masterConfig: Record<TabKey, MasterConfig> = {
  BRAND: {
    path: 'brand',
    label: 'Brand',
    title: 'Brand',
    nameKey: 'brandName',
    searchPlaceholder: 'Brand name...',
    fields: [
      { key: 'brandName', label: 'Brand Name', type: 'text', required: true, partOfDuplicateKey: true },
      { key: 'description', label: 'Description', type: 'text' },
    ],
    columns: [
      { field: 'brandName', headerName: 'Brand', minWidth: 150, ...centred },
      { field: 'description', headerName: 'Description', minWidth: 180, ...centred },
    ],
  },

  MODEL: {
    path: 'model',
    label: 'Model',
    title: 'Model',
    nameKey: 'modelName',
    searchPlaceholder: 'Model or brand...',
    fields: [
      { key: 'brandId', label: 'Brand', type: 'select', source: 'brands', required: true, partOfDuplicateKey: true },
      { key: 'modelName', label: 'Model Name', type: 'text', required: true, partOfDuplicateKey: true },
      { key: 'description', label: 'Description', type: 'text' },
    ],
    columns: [
      { field: 'brandName', headerName: 'Brand', minWidth: 130, ...centred },
      { field: 'modelName', headerName: 'Model', minWidth: 150, ...centred },
      { field: 'description', headerName: 'Description', minWidth: 180, ...centred },
    ],
  },

  EMI_BANK: {
    path: 'emi-bank',
    label: 'EMI Bank',
    title: 'EMI Bank',
    nameKey: 'bankName',
    searchPlaceholder: 'Bank or contact...',
    fields: [
      { key: 'bankName', label: 'Bank Name', type: 'text', required: true, partOfDuplicateKey: true },
      { key: 'contactPerson', label: 'Contact Person', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'phone' },
      { key: 'address', label: 'Address', type: 'text' },
    ],
    columns: [
      { field: 'bankName', headerName: 'Bank', minWidth: 180, ...centred },
      { field: 'contactPerson', headerName: 'Contact Person', minWidth: 150, ...centred },
      { field: 'phone', headerName: 'Phone', minWidth: 130, ...centred },
    ],
  },

  SUPPLIER: {
    path: 'supplier',
    label: 'Supplier',
    title: 'Supplier',
    nameKey: 'supplierName',
    searchPlaceholder: 'Supplier, phone or GSTIN...',
    fields: [
      { key: 'supplierName', label: 'Supplier Name', type: 'text', required: true, partOfDuplicateKey: true },
      { key: 'phone', label: 'Phone', type: 'phone' },
      { key: 'address', label: 'Address', type: 'text' },
      { key: 'gstin', label: 'GSTIN', type: 'text' },
    ],
    columns: [
      { field: 'supplierName', headerName: 'Supplier', minWidth: 180, ...centred },
      { field: 'phone', headerName: 'Phone', minWidth: 130, ...centred },
      { field: 'gstin', headerName: 'GSTIN', minWidth: 150, ...centred },
    ],
  },

  CUSTOMER: {
    path: 'customer',
    label: 'Customer',
    title: 'Customer',
    nameKey: 'customerName',
    searchPlaceholder: 'Customer, mobile or GSTIN...',
    fields: [
      { key: 'customerName', label: 'Customer Name', type: 'text', required: true },
      { key: 'mobileNumber', label: 'Mobile', type: 'phone', partOfDuplicateKey: true },
      { key: 'address', label: 'Address', type: 'text' },
      // Decides the tax on their bills: the shop's own state means CGST + SGST,
      // anywhere else means IGST.
      { key: 'stateCode', label: 'State', type: 'state', nameKey: 'state' },
      { key: 'gstin', label: 'GSTIN', type: 'text' },
    ],
    columns: [
      { field: 'customerName', headerName: 'Customer', minWidth: 180, ...centred },
      { field: 'mobileNumber', headerName: 'Mobile', minWidth: 140, ...centred },
      { field: 'address', headerName: 'Address', minWidth: 180, ...centred },
      { field: 'state', headerName: 'State', minWidth: 130, ...centred },
    ],
  },
};

export const tabOrder: TabKey[] = ['BRAND', 'MODEL', 'EMI_BANK', 'SUPPLIER', 'CUSTOMER'];
